import {
  DatePicker,
  LocalizationProvider,
  StaticDatePicker,
  StaticTimePicker,
  TimePicker,
} from "@mui/x-date-pickers";
import {
  Popover,
  TextField,
  PopoverProps,
  styled,
  Tooltip,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  allTimeFilter,
  customTimeFilter,
  globalMinimumDate,
} from "@pages/BI/Audit/const";
import Button from "@common/components/Button";
import { isSameDay } from "date-fns";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
interface DateForm {
  start: Date;
  end: Date;
}

enum DisplayPicker {
  "StartDate",
  "StartTime",
  "EndDate",
  "EndTime",
}

const SIZE_DATE_TEXTFIELD = 22;

const PopoverGrid = styled(Grid)(({ theme }) => ({
  "&.MuiGrid2-root": {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(0),
  },
  "& .MuiPickerStaticWrapper-root": {
    marginBottom: theme.spacing(2),
  },
  "& .MuiFormControl-root": {
    width: theme.spacing(SIZE_DATE_TEXTFIELD),
  },
}));

export type CustomDateFilter = {
  label: string;
  getDates: () => DateInterval;
};
type DateSelectorType = {
  timeFilter: CustomDateFilter;
  open: boolean;
  onClose: (newFilter?: CustomDateFilter) => void;
  onRemove?: () => void;
  isValidRange?: (start: Date, end: Date) => boolean;
  errorInvalidRange?: string;
  anchorEl: PopoverProps["anchorEl"];
};

const dateTimeDefault = (defaultDate: CustomDateFilter) => ({
  start: new Date(defaultDate.getDates().startDate),
  end: new Date(defaultDate.getDates().endDate),
});
const DatePopover = (props: React.PropsWithChildren<DateSelectorType>) => {
  const {
    timeFilter,
    open,
    anchorEl,
    onClose,
    onRemove,
    isValidRange = (start: Date, end: Date) => start < end,
    errorInvalidRange,
  } = props;

  const [dates, setDates] = useState<DateForm>(dateTimeDefault(timeFilter));

  const [picker, setPicker] = useState<DisplayPicker | undefined>();
  const [errors, setErrors] = useState({});
  const { t } = useTranslation();

  const isValid = Object.values(errors).reduce(
    (acc, value) => acc && value === null,
    true
  );

  useEffect(() => {
    setDates((prevDates) => prevDates || dateTimeDefault(timeFilter));
  }, [timeFilter]);

  const changePicker = (value: DisplayPicker) =>
    setPicker(value === picker ? undefined : value);

  const onError = (name: DisplayPicker) => (error: string | null) => {
    setErrors((old) => ({ ...old, [name]: error }));
  };

  const handleClearButton = () => {
    setDates(dateTimeDefault(allTimeFilter));
    if (onRemove) onRemove();
    onClose();
  };

  const getPicker = () => {
    switch (picker) {
      case DisplayPicker.StartDate:
        return (
          <StaticDatePicker
            displayStaticWrapperAs="desktop"
            value={dates.start}
            minDate={new Date(globalMinimumDate)}
            maxDate={dates.end}
            onChange={(newValue: Date | null) => {
              if (newValue) {
                setDates({ ...dates, start: newValue });
              }
            }}
          />
        );
      case DisplayPicker.StartTime:
        return (
          <StaticTimePicker
            value={dates.start}
            maxTime={isSameDay(dates.start, dates.end) ? dates.end : undefined}
            onChange={(newValue: Date | null) => {
              if (newValue) {
                setDates({ ...dates, start: newValue });
              }
            }}
          />
        );
      case DisplayPicker.EndDate:
        return (
          <StaticDatePicker
            displayStaticWrapperAs="desktop"
            minDate={new Date(globalMinimumDate)}
            value={dates.end}
            onChange={(newValue: Date | null) => {
              if (newValue) {
                setDates({ ...dates, end: newValue });
              }
            }}
          />
        );
      case DisplayPicker.EndTime:
        return (
          <StaticTimePicker
            value={dates.end}
            minTime={
              isSameDay(dates.start, dates.end) ? dates.start : undefined
            }
            onChange={(newValue: Date | null) => {
              if (newValue) {
                setDates({ ...dates, end: newValue });
              }
            }}
          />
        );
      case undefined:
      default:
        return "";
    }
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={() => onClose()}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "center",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "center",
      }}
    >
      <PopoverGrid
        container
        spacing={2}
        direction="column"
        sx={{
          alignItems: "center",
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Grid>
            <Grid container direction="row" sx={{ width: "100%", gap: 2 }}>
              <DatePicker
                label={t("From")}
                open={false}
                value={dates.start}
                onChange={(newValue: Date | null) => {
                  if (newValue) {
                    setDates({ ...dates, start: newValue });
                  }
                }}
                onOpen={() => changePicker(DisplayPicker.StartDate)}
                onError={onError(DisplayPicker.StartDate)}
              />
              <TimePicker
                open={false}
                value={dates.start}
                onChange={(newValue: Date | null) => {
                  if (newValue) {
                    setDates({ ...dates, start: newValue });
                  }
                }}
                onOpen={() => changePicker(DisplayPicker.StartTime)}
                onError={onError(DisplayPicker.StartTime)}
              />
            </Grid>
          </Grid>
          <Grid>
            <Grid container direction="row" sx={{ width: "100%", gap: 2 }}>
              <DatePicker
                label={t("To")}
                open={false}
                value={dates.end}
                onChange={(newValue: Date | null) => {
                  if (newValue) {
                    setDates({ ...dates, end: newValue });
                  }
                }}
                onOpen={() => changePicker(DisplayPicker.EndDate)}
                onError={onError(DisplayPicker.EndDate)}
              />
              <TimePicker
                open={false}
                value={dates.end}
                onChange={(newValue: Date | null) => {
                  if (newValue) {
                    setDates({ ...dates, end: newValue });
                  }
                }}
                onOpen={() => changePicker(DisplayPicker.EndTime)}
                onError={onError(DisplayPicker.EndTime)}
              />
            </Grid>
          </Grid>
          {getPicker()}
        </LocalizationProvider>
        <Grid
          container
          direction="row"
          spacing={2}
          sx={{ width: "100%", justifyContent: "end", marginBottom: 2 }}
        >
          {onRemove !== undefined && (
            <Grid>
              <Button
                onClick={() => handleClearButton()}
                color="greyed"
                variant="outlined"
              >
                {t("Clear")}
              </Button>
            </Grid>
          )}

          <Grid>
            <Tooltip
              title={
                !isValidRange(dates.start, dates.end) && errorInvalidRange
                  ? t(errorInvalidRange)
                  : ""
              }
            >
              <span>
                <Button
                  disabled={!isValid || !isValidRange(dates.start, dates.end)}
                  onClick={() => {
                    onClose({
                      ...customTimeFilter,
                      getDates: (): { startDate: number; endDate: number } => ({
                        startDate: dates.start.getTime(),
                        endDate: dates.end.getTime(),
                      }),
                    });
                  }}
                  variant="contained"
                  color="white"
                >
                  OK
                </Button>
              </span>
            </Tooltip>
          </Grid>
        </Grid>
      </PopoverGrid>
    </Popover>
  );
};

export default DatePopover;
